import { kml } from '@tmcw/togeojson';
import {
  polygon as turfPolygon, featureCollection, kinks, intersect, area as turfArea, bbox as turfBbox,
} from '@turf/turf';

// Parse + validate KML polygons and classify them against the buildings
// already known to the caller. Pure: no DOM mutation, no network, no UI -
// the panel orchestrates the user flow on top of these primitives.
//
// We deliberately keep only OUTER polygon rings and report how many inner
// "holes" were dropped. The existing buildings schema is geometry(Polygon,4326)
// with no hole support, and FAT-coverage building polygons are simple outlines
// in practice. KML MultiGeometry yields several polygons; each becomes its own
// imported polygon. Non-polygon features (Point/LineString) are ignored.

function* polygonRingsOf(feature) {
  const g = feature && feature.geometry;
  if (!g) return;
  if (g.type === 'Polygon') {
    yield g.coordinates;
  } else if (g.type === 'MultiPolygon') {
    for (const poly of g.coordinates) yield poly;
  }
}

function validRing(ring) {
  if (!Array.isArray(ring) || ring.length < 4) return false; // 3 unique + closure
  for (const c of ring) {
    if (!Array.isArray(c) || c.length < 2) return false;
    const [lng, lat] = c;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return false;
  }
  const f = ring[0]; const l = ring[ring.length - 1];
  return f[0] === l[0] && f[1] === l[1];
}

// xmlString -> {
//   valid: [{ geometry, name, index }],
//   invalid: [{ reason, index }],
//   holesDropped,
// }.
// Each `geometry` is a GeoJSON Polygon with only the outer ring, ready to feed
// to createBuilding(). `index` is the polygon's position across the whole file
// (counting MultiGeometry expansions) so the UI can refer back to a row.
export function parseKmlToPolygons(xmlString, opts = {}) {
  const DOMParserCtor = opts.DOMParser
    || (typeof DOMParser !== 'undefined' ? DOMParser : null);
  if (!DOMParserCtor) {
    throw new Error('DOMParser not available; pass one via opts.DOMParser in non-browser envs.');
  }
  const valid = [];
  const invalid = [];
  let holesDropped = 0;

  if (typeof xmlString !== 'string' || xmlString.trim() === '') {
    invalid.push({ reason: 'parse-failed', index: 0 });
    return { valid, invalid, holesDropped };
  }

  let dom;
  try { dom = new DOMParserCtor().parseFromString(xmlString, 'text/xml'); }
  catch (e) {
    invalid.push({ reason: 'parse-failed', index: 0 });
    return { valid, invalid, holesDropped };
  }

  // DOMParser does not throw on malformed XML in browsers / jsdom - it returns
  // a document containing a <parsererror> element. Detect it explicitly so the
  // caller sees "parse-failed" instead of an empty FeatureCollection.
  if (dom && typeof dom.getElementsByTagName === 'function'
    && dom.getElementsByTagName('parsererror').length > 0) {
    invalid.push({ reason: 'parse-failed', index: 0 });
    return { valid, invalid, holesDropped };
  }

  let fc;
  try { fc = kml(dom); }
  catch (e) {
    invalid.push({ reason: 'parse-failed', index: 0 });
    return { valid, invalid, holesDropped };
  }
  const features = (fc && fc.features) || [];

  let i = -1;
  for (const f of features) {
    let yielded = false;
    for (const polyCoords of polygonRingsOf(f)) {
      yielded = true;
      i += 1;
      holesDropped += Array.isArray(polyCoords) ? Math.max(0, polyCoords.length - 1) : 0;
      const outerRaw = polyCoords && polyCoords[0];
      if (!validRing(outerRaw)) { invalid.push({ reason: 'invalid-ring', index: i }); continue; }
      // togeojson preserves KML's third coordinate (altitude) when present.
      // Our PostGIS column is geometry(Polygon, 4326) - strictly 2D - so a
      // 3D polygon fails the insert cast with "Geometry has Z dimension but
      // column does not". Strip altitude unconditionally so the output is
      // always [[lng, lat], ...].
      const outer = outerRaw.map(([lng, lat]) => [lng, lat]);

      const geometry = { type: 'Polygon', coordinates: [outer] };
      // Self-intersection guard (matches the live-draw rule in AnnotationMap).
      try {
        const kk = kinks(turfPolygon(geometry.coordinates));
        if (kk.features.length > 0) {
          invalid.push({ reason: 'self-intersecting', index: i });
          continue;
        }
      } catch (e) {
        invalid.push({ reason: 'invalid-shape', index: i });
        continue;
      }

      const name = (f.properties && f.properties.name) || null;
      valid.push({ geometry, name, index: i });
    }
    // Non-polygon features (Point/LineString/etc.) are not even errors - they
    // are just not what we are importing. They go silently uncounted.
    if (!yielded && f && f.geometry && f.geometry.type !== 'Point'
      && f.geometry.type !== 'MultiPoint' && f.geometry.type !== 'LineString'
      && f.geometry.type !== 'MultiLineString') {
      i += 1;
      invalid.push({ reason: 'unsupported-geometry', index: i });
    }
  }
  return { valid, invalid, holesDropped };
}

// Envelope { west, south, east, north } covering every polygon in `polygons`,
// or null if empty. Used as the root tile for the complete bbox loader.
export function bboxOf(polygons) {
  if (!polygons || polygons.length === 0) return null;
  const fc = featureCollection(polygons.map((p) => turfPolygon(p.geometry.coordinates)));
  const [west, south, east, north] = turfBbox(fc);
  return { west, south, east, north };
}

// Bucket each parsed polygon against rows already in the database for the
// import bbox (the rows returned by buildings_in_bbox, each carrying id,
// can_manage and geometry). An overlap means a POSITIVE shared area; shared
// edges/walls have zero intersection area and are allowed (mirrors the
// server-side buildings_no_overlap trigger).
//   - clean       : no overlap with any row
//   - replaceable : overlaps only with rows the caller can_manage (own polys,
//                   plus any row when the caller is admin) - replace = delete
//                   those rows then insert the imported polygon
//   - blocked     : overlaps with at least one row the caller cannot manage
//                   (always skipped on import; only an admin or the owning
//                   operator could resolve it)
// `mineCount` / `otherCount` are returned as hints so the UI can write an
// honest summary even when admin sees zero "blocked". `intersectCalls` exposes
// how many exact Turf intersections survived the spatial prefilter.
export function classifyOverlaps(polygons, existing) {
  const clean = [];
  const replaceable = [];
  const blocked = [];
  const CELL = 0.001;
  const existingPolys = (existing || [])
    .map((e) => {
      try {
        const p = turfPolygon(e.geometry.coordinates);
        return { e, p, bbox: turfBbox(p) };
      } catch (err) { return null; }
    })
    .filter(Boolean);

  // A small uniform spatial grid keeps Turf away from pairs whose envelopes
  // cannot possibly meet. Store indexes (rather than objects) so a candidate's
  // gathered union can be sorted back into the caller's original row order.
  const grid = new Map();
  const cellKey = (x, y) => `${x}:${y}`;
  existingPolys.forEach(({ bbox }, index) => {
    const minX = Math.floor(bbox[0] / CELL);
    const minY = Math.floor(bbox[1] / CELL);
    const maxX = Math.floor(bbox[2] / CELL);
    const maxY = Math.floor(bbox[3] / CELL);
    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        const key = cellKey(x, y);
        const bucket = grid.get(key);
        if (bucket) bucket.push(index); else grid.set(key, [index]);
      }
    }
  });

  let mineCount = 0; // imported polygons that touched at least one of MY rows
  let otherCount = 0; // ... touched at least one row I cannot manage
  let intersectCalls = 0;

  for (const cand of (polygons || [])) {
    let candP;
    let candBbox;
    try {
      candP = turfPolygon(cand.geometry.coordinates);
      candBbox = turfBbox(candP);
    } catch (err) { clean.push({ ...cand, overlapIds: [] }); continue; }

    const gathered = new Set();
    const minX = Math.floor(candBbox[0] / CELL);
    const minY = Math.floor(candBbox[1] / CELL);
    const maxX = Math.floor(candBbox[2] / CELL);
    const maxY = Math.floor(candBbox[3] / CELL);
    for (let x = minX; x <= maxX; x += 1) {
      for (let y = minY; y <= maxY; y += 1) {
        const bucket = grid.get(cellKey(x, y));
        if (bucket) bucket.forEach((index) => gathered.add(index));
      }
    }

    const ids = [];
    let touchedMine = false;
    let touchedOther = false;

    const possibleIndexes = Array.from(gathered).sort((a, b) => a - b);
    for (const index of possibleIndexes) {
      const { e, p, bbox } = existingPolys[index];
      const bboxesOverlap = !(bbox[2] < candBbox[0] || bbox[0] > candBbox[2]
        || bbox[3] < candBbox[1] || bbox[1] > candBbox[3]);
      if (!bboxesOverlap) continue;

      let inter = null;
      intersectCalls += 1;
      try { inter = intersect(featureCollection([candP, p])); }
      catch (err) { inter = null; }
      if (inter && turfArea(inter) > 0) {
        ids.push(e.id);
        if (e.can_manage) touchedMine = true; else touchedOther = true;
      }
    }
    if (touchedMine) mineCount += 1;
    if (touchedOther) otherCount += 1;

    const item = { ...cand, overlapIds: ids };
    if (ids.length === 0) clean.push(item);
    else if (touchedOther) blocked.push(item);
    else replaceable.push(item);
  }
  return {
    clean, replaceable, blocked, mineCount, otherCount, intersectCalls,
  };
}
